interface LabelText {
  primaryText: string;
  priorityText?: string;
}

/**
 * The words of `text`, lowercased. Split by Unicode letter and digit class
 * rather than `\b`, which treats an accented letter as a boundary and so would
 * not find "Planifié" at the end of a string.
 */
const words = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/** Whether `phrase` occurs in `text` as a run of whole words. */
const containsPhrase = (text: string, phrase: string[]): boolean => {
  const haystack = words(text);
  return haystack.some((_, start) =>
    phrase.every((word, offset) => haystack[start + offset] === word),
  );
};

/**
 * The status icon's accessible name: hire's localized status text, so a screen
 * reader hears the state on a chip whose text is only a stage title (a scheduled
 * session, a video recording). Omitted when the chip's text already carries that
 * word, because the icon would otherwise announce the state a second time.
 *
 * Matched as whole words, so a negated status does not hide its opposite:
 * "Complete" is not carried by "Incomplete", nor "complet" by "incomplet".
 */
export const statusIconLabel = (
  statusText: string | undefined,
  { primaryText, priorityText }: LabelText,
): string | undefined => {
  const state = statusText?.trim();
  if (!state) return undefined;

  const phrase = words(state);
  const carried =
    containsPhrase(primaryText, phrase) ||
    (priorityText !== undefined && containsPhrase(priorityText, phrase));

  return carried ? undefined : state;
};
